import * as d3 from 'd3';
import * as utilities from "../utilities.js";

import { num_of_dataset } from '../data_processing/number_of_data.js';
import { number_growth_float } from '../animation/number.js';
import data_processing from "../data_processing/index.js";

/**
 * Retrieves the number of datasets for the given data and days.
 * If a TypeError occurs, it attempts to fetch data from the previous month.
 *
 * @param {Object} data - The data object to process.
 * @param {number} days - The number of days to consider.
 * @returns {Promise<number>} - A promise that resolves to the number of datasets.
 */
async function get_num_of_dataset(data, days) {
    try {
        return await num_of_dataset(data, days);
    } catch (error) {
        if (error instanceof TypeError) {
            const current_month = data_processing.file.get_current_date("yyyy-mm");
            const last_month = data_processing.file.get_last_month(current_month);
            const last_stats_data = await utilities.load_daily_stats_data(last_month);
            return await num_of_dataset(last_stats_data, days === 1 ? 0 : Math.abs(Number(data_processing.file.get_current_date("yyyymmdd").slice(-2)) - 7));
        }
    }
}

/**
 * Generates the main card displaying the current number of datasets and the differences from yesterday and last week.
 *
 * @param {Array} data - The data array.
 */
export async function main_card(data) {
    const cur_div = document.getElementById('main-card');
    let cur_num = await num_of_dataset(data, 0);
    let yesterday_num = await get_num_of_dataset(data, 1);
    let last_week_num = await get_num_of_dataset(data, 7);

    let day_diff = cur_num - yesterday_num;
    let week_diff = cur_num - last_week_num;

    let day_diff_str = day_diff >= 0 ? `+ ${day_diff}` : `${day_diff}`;
    let week_diff_str = week_diff >= 0 ? `+ ${week_diff}` : `${week_diff}`;

    function animateValue(element, start, end, duration) {
        let startTime = null;

        function step(timestamp) {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const value = Math.floor(progress * (end - start) + start);
            element.innerText = value;
            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
        }

        window.requestAnimationFrame(step);
    }

    cur_div.innerHTML = `
        <h5 class="card-title-light" data-i18n="current-dataset-num">資料集總數</h5>
        <h1 id="current-number" class="card-title-light">0</h1></br>
        <h5 class="card-title-light" data-i18n="comp-yesterday" style="text-align: right">與昨天相比</h5>
        <h2 class="card-title-light" style="text-align: right">${day_diff_str}</h2></br>
        <h5 class="card-title-light" data-i18n="comp-laset-week" style="text-align: right">與上週相比</h5>
        <h2 class="card-title-light" style="text-align: right">${week_diff_str}</h2></br>
    `;
    $(cur_div).localize();

    const currentNumberElement = document.getElementById('current-number');
    animateValue(currentNumberElement, 0, cur_num, 800);
}

/**
 * Generates a statistics card and appends it to a specified container.
 *
 * @param {string} container_id - The ID of the container element.
 * @param {string} heading_text - The heading text for the card.
 * @param {number} number - The number to display in the card.
 */
export function stats_card(container_id, heading_text, number, decimal_places = 2) {
    const container = document.getElementById(container_id);
    let num = document.getElementById(`${container_id}_num`);
    
    if (!num) {
        let div = document.createElement('div');
        let heading = document.createElement('h5');
        heading.textContent = heading_text;
        heading.className = 'card-title-dark';
        heading.setAttribute('data-i18n', `${container_id}_heading`);

        num = document.createElement('p');
        num.textContent = number;
        num.className = 'card-big-num fs-2';
        num.setAttribute('id', `${container_id}_num`);

        div.appendChild(heading);
        div.appendChild(num);
        container.appendChild(div);
        $(container).localize();

        number_growth_float(num, 0, number, 800, decimal_places);
    } else {
        num.textContent = `${number}`;

        //Animation
        number_growth_float(num, 0, number, 800, decimal_places);
    }
}

/**
 * Generates cards based on a mapping object and appends them to a specified container.
 *
 * @param {string} container_id - The ID of the container element.
 * @param {Object} mapping - The mapping object containing names and display texts.
 */
export function gen_cards_by_map(container_id, mapping) {
    const container = document.getElementById(container_id);

    for (let name in mapping) {
        // Create Elements
        let col = document.createElement('div');
        col.className = 'col';

        let card = document.createElement('div');
        card.className = 'card rounded-4 my-2 border-0 main-bg h-100';

        let card_body = document.createElement('div');
        card_body.className = 'card-body'

        let heading = document.createElement('h6');
        heading.className = 'card-title-dark';
        heading.setAttribute("data-i18n", `md_rl_${name}`);
        heading.textContent = mapping[name];
        $(heading).localize();

        setTimeout(() => {
            let text = heading.textContent.trim();
            let words = text.split(/\s+/);
            if (localStorage.lang == "en" && words.length < 2) {
                heading.innerHTML = `${text}</br></br>`;
            }
        }, 0);

        // Draw the Chart Div
        let chartWrapper = document.createElement('div');
        chartWrapper.className = 'chart-wrapper d-flex justify-content-center align-items-center flex-grow-1';

        let chart = document.createElement('div');
        chart.id = `${name}_chart`;

        chartWrapper.appendChild(chart);

        card_body.appendChild(heading);
        card_body.appendChild(chartWrapper);
        card.appendChild(card_body);
        col.appendChild(card);
        container.appendChild(col);
    }
}